/**
 * AssignmentDialog — admin/SME/instructor authors the per-chapter assignment.
 *
 * Fields:
 *   • Title (required)
 *   • Description (markdown-friendly textarea)
 *   • Rubric (helpful for graders)
 *   • Max marks (default 25)
 *   • Pass mark (optional; not enforced on unlock, only used in reporting)
 *   • Estimated hours (default 10) — sets the student's timer window
 *   • Reference brief attachment (optional; any file type ≤50MB)
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Upload, FileText, Loader2, Save } from "lucide-react";

const BLANK = {
    title: "",
    description: "",
    rubric: "",
    max_marks: 25,
    pass_mark: "",
    estimated_hours: 10,
};

function fmtError(e, fb) { return e?.response?.data?.detail || e?.message || fb; }

export default function AssignmentDialog({ open, onOpenChange, chapterId, chapterTitle, onSaved }) {
    const [form, setForm] = useState(BLANK);
    const [existing, setExisting] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!open || !chapterId) return;
        let cancelled = false;
        setLoading(true);
        api.get(`/admin/chapters/${chapterId}/assignment`)
            .then(({ data }) => {
                if (cancelled) return;
                const a = data?.assignment;
                setExisting(a);
                if (a) {
                    setForm({
                        title: a.title || "",
                        description: a.description || "",
                        rubric: a.rubric || "",
                        max_marks: a.max_marks ?? 25,
                        pass_mark: a.pass_mark ?? "",
                        estimated_hours: a.estimated_hours ?? 10,
                    });
                } else {
                    setForm(BLANK);
                }
            })
            .catch((e) => toast.error(fmtError(e, "Could not load assignment")))
            .finally(() => setLoading(false));
        return () => { cancelled = true; };
    }, [open, chapterId]);

    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Please give the assignment a title.");
            return;
        }
        if (form.pass_mark !== "" && Number(form.pass_mark) > Number(form.max_marks)) {
            toast.error("Pass mark cannot exceed the maximum marks.");
            return;
        }
        setSaving(true);
        try {
            const body = {
                title: form.title.trim(),
                description: form.description,
                rubric: form.rubric,
                max_marks: Number(form.max_marks) || 25,
                estimated_hours: Number(form.estimated_hours) || 10,
                pass_mark: form.pass_mark === "" ? null : Number(form.pass_mark),
            };
            const { data } = await api.post(
                `/admin/chapters/${chapterId}/assignment`,
                body,
            );
            toast.success(data.created ? "Assignment created" : "Assignment updated");
            // Re-fetch to hydrate `existing.attachment` if any
            const fresh = await api.get(`/admin/chapters/${chapterId}/assignment`);
            setExisting(fresh.data?.assignment);
            onSaved?.();
        } catch (e) {
            toast.error(fmtError(e, "Could not save assignment"));
        } finally {
            setSaving(false);
        }
    };

    const uploadAttachment = async (file) => {
        if (!existing?.id) {
            toast.error("Save the assignment first, then upload the reference file.");
            return;
        }
        if (file.size > 50 * 1024 * 1024) {
            toast.error("File must be smaller than 50 MB.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/admin/assignments/${existing.id}/attachment`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setExisting((s) => ({ ...s, attachment: data.attachment }));
            toast.success("Reference file uploaded");
        } catch (e) {
            toast.error(fmtError(e, "Could not upload attachment"));
        } finally {
            setUploading(false);
        }
    };

    const removeAttachment = async () => {
        if (!existing?.id || !existing.attachment) return;
        if (!window.confirm("Remove the reference file? Students will no longer see it.")) return;
        try {
            await api.delete(`/admin/assignments/${existing.id}/attachment`);
            setExisting((s) => ({ ...s, attachment: null }));
            toast.success("Reference file removed");
        } catch (e) {
            toast.error(fmtError(e, "Could not remove attachment"));
        }
    };

    const del = async () => {
        if (!existing?.id) return;
        if (!window.confirm("Delete this assignment? Past student submissions will be preserved but this chapter will no longer require an assignment.")) return;
        try {
            await api.delete(`/admin/assignments/${existing.id}`);
            toast.success("Assignment removed");
            setExisting(null);
            setForm(BLANK);
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(fmtError(e, "Could not delete"));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="assignment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-[#134B30]">
                        Chapter assignment
                    </DialogTitle>
                    <DialogDescription>
                        {chapterTitle ? <>Assignment for <strong>{chapterTitle}</strong>.</> : "Configure the assignment for this chapter."}
                        {" "}Students must submit this before the next chapter unlocks.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-12 text-center text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </div>
                ) : (
                    <div className="space-y-4 mt-2">
                        <div>
                            <Label>Title *</Label>
                            <Input
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="e.g. Case study — Business ethics"
                                data-testid="assignment-title"
                            />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                rows={6}
                                placeholder="Explain the deliverable in detail. Markdown-style newlines work here."
                                data-testid="assignment-description"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Shown to students in the assignment panel.
                            </p>
                        </div>
                        <div>
                            <Label>Grading rubric</Label>
                            <Textarea
                                value={form.rubric}
                                onChange={(e) => setForm({ ...form, rubric: e.target.value })}
                                rows={4}
                                placeholder="e.g. 10 marks for analysis, 10 for structure, 5 for citations."
                                data-testid="assignment-rubric"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Helps graders stay consistent. Also visible to the student.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <Label>Max marks *</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={1000}
                                    value={form.max_marks}
                                    onChange={(e) => setForm({ ...form, max_marks: e.target.value })}
                                    data-testid="assignment-max-marks"
                                />
                            </div>
                            <div>
                                <Label>Pass mark (optional)</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    max={Number(form.max_marks) || 100}
                                    value={form.pass_mark}
                                    onChange={(e) => setForm({ ...form, pass_mark: e.target.value })}
                                    placeholder="e.g. 15"
                                    data-testid="assignment-pass-mark"
                                />
                            </div>
                            <div>
                                <Label>Estimated hours *</Label>
                                <Input
                                    type="number"
                                    step="0.5"
                                    min={0.5}
                                    max={200}
                                    value={form.estimated_hours}
                                    onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })}
                                    data-testid="assignment-hours"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Student&apos;s upload button unlocks after this many hours.
                                </p>
                            </div>
                        </div>

                        {/* Reference file attachment (any file type). Kept
                          * visible even before the assignment is saved so
                          * authors see it right away — the input is disabled
                          * with a hint until an assignment id exists. */}
                        <div className="border border-border rounded p-3 bg-muted/20">
                            <Label className="mb-2 inline-block">Reference file (optional)</Label>
                            <p className="text-xs text-muted-foreground mb-2">
                                Attach an assignment brief, source doc, or dataset. Any file type, max 50&nbsp;MB. Students see this only after they click <em>Start assignment</em>.
                            </p>
                            {!existing?.id ? (
                                <div
                                    className="inline-flex items-center gap-1.5 text-sm border border-dashed border-border/70 rounded px-3 py-2 text-muted-foreground bg-background/40 cursor-not-allowed"
                                    data-testid="assignment-attach-hint"
                                >
                                    <Upload className="h-4 w-4" />
                                    <span>Save this assignment first to enable file upload</span>
                                </div>
                            ) : existing.attachment ? (
                                <div className="flex items-center justify-between gap-2 text-sm">
                                    <span className="inline-flex items-center gap-1.5 truncate">
                                        <FileText className="h-4 w-4 text-[#134B30]" />
                                        <span className="truncate">{existing.attachment.filename}</span>
                                        <span className="text-xs text-muted-foreground">
                                            ({Math.round(existing.attachment.size / 1024)} KB)
                                        </span>
                                    </span>
                                    <div className="flex items-center gap-3">
                                        <label className="cursor-pointer text-xs text-[#134B30] hover:underline">
                                            Replace
                                            <input
                                                type="file"
                                                className="hidden"
                                                onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])}
                                                data-testid="assignment-attach-replace"
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={removeAttachment}
                                            className="text-xs text-rose-700 hover:underline"
                                            data-testid="assignment-attach-remove"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <label className="inline-flex items-center gap-1.5 text-sm border border-dashed border-border rounded px-3 py-2 cursor-pointer hover:bg-background transition">
                                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    <span>Upload reference file (≤ 50 MB)</span>
                                    <input
                                        type="file"
                                        className="hidden"
                                        onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])}
                                        data-testid="assignment-attach"
                                    />
                                </label>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter className="mt-4 flex items-center justify-between gap-2">
                    <div>
                        {existing?.id && (
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={del}
                                className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                                data-testid="assignment-delete"
                            >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Remove assignment
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="bg-[#134B30] hover:bg-[#0E3A24] text-white gap-1.5"
                            data-testid="assignment-save"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {existing?.id ? "Save changes" : "Create assignment"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
